// This module sends only finite, source-controlled event fields. It never reads
// form values, request bodies, response bodies or identifiers. Labels are
// compared locally to an explicit map; their raw values are never emitted.
const OPERATIONS = new Set([
  'task_create',
  'task_update',
  'task_publish',
  'task_close',
  'draft_or_submission_create',
  'submission_update',
  'submission_delete',
  'submission_submit',
  'feedback_request',
  'feedback_comment',
  'rubric_create',
  'rubric_update',
  'comment_bank_change',
  'success_criteria_change',
  'short_answer_change',
  'class_change',
  'settings_change',
  'exam_create',
  'exam_update',
  'exam_ingestion_request',
  'exam_marking_request',
  'exam_marking',
  'exam_ranking',
  'exam_export',
  'exam_complete',
  'submission_export',
  'feedback_generation',
  'handwritten_upload',
  'stimulus_upload',
  'credits_checkout_request',
  'credits_invoice_request',
  'credits_callback_request',
  'credits_granted',
  'task_reopen',
  'task_delete',
  'submission_batch',
  'extraction_request',
  'feedback_delivery_retry'
]);
const PHASES = new Set([
  'attempt',
  'success',
  'failure',
  'accepted',
  'unconfirmed',
  'completed',
  'partial'
]);
const CONTROLS = new Set([
  'button',
  'link',
  'tab',
  'checkbox',
  'select',
  'form',
  'text_input',
  'surface',
  'other'
]);
const INTERACTIONS = new Set(['click', 'change', 'submit']);
const TARGETS = Object.freeze({
  new: 'new',
  'new task': 'new_task',
  'create task': 'create_task',
  'new draft': 'new_draft',
  'my drafts': 'drafts',
  tasks: 'tasks',
  'current tasks': 'current_tasks',
  'closed tasks': 'closed_tasks',
  rubrics: 'rubrics',
  'comment banks': 'comment_banks',
  'success criteria': 'success_criteria',
  'short answers': 'short_answers',
  'marking tools': 'marking_tools',
  'class insights': 'class_insights',
  classes: 'classes',
  home: 'home',
  library: 'library',
  filters: 'filters',
  'filters:': 'filters',
  'sort by': 'sort',
  column: 'column_view',
  calendar: 'calendar_view',
  'get feedback': 'get_feedback',
  'request feedback': 'request_feedback',
  submit: 'submit',
  'submit response': 'submit_response',
  'mark next': 'mark_next',
  'review work': 'review_work',
  'open task': 'open_task',
  'upload responses': 'upload_responses',
  'edit task': 'edit_task',
  save: 'save',
  'save changes': 'save',
  publish: 'publish',
  'publish task': 'publish_task',
  download: 'download',
  export: 'export',
  share: 'share',
  delete: 'delete',
  cancel: 'cancel',
  close: 'close',
  next: 'next',
  back: 'back',
  search: 'search',
  settings: 'settings',
  account: 'account',
  'log out': 'logout',
  'mark an exam': 'mark_exam',
  'new class task': 'new_task',
  'new rubric': 'new_rubric',
  'new comment bank': 'new_comment_bank',
  'new success criteria': 'new_success_criteria',
  'new short answer': 'new_short_answer',
  'all tasks': 'all_tasks',
  'give feedback': 'give_feedback',
  dismiss: 'dismiss'
});
const TARGET_VALUES = new Set(Object.values(TARGETS));
const PATH_TAGS = new Set([
  'a',
  'button',
  'div',
  'span',
  'input',
  'select',
  'textarea',
  'form',
  'label',
  'li',
  'nav',
  'section',
  'article',
  'main',
  'svg',
  'path'
]);
let sink = () => {};
let enabled = false;
let installed = false;
let canRecord = () => true;
let correlationContext = () => null;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const createInteractionLimiter = (clock = Date.now) => {
  let tokens = 120;
  let last = clock();
  return () => {
    const now = Math.max(last, clock());
    tokens = Math.min(120, tokens + ((now - last) * 2) / 1000);
    last = now;
    if (tokens < 1) return false;
    tokens -= 1;
    return true;
  };
};
const interactionAllowed = createInteractionLimiter();
const recordingActive = () => {
  try {
    return enabled && canRecord();
  } catch {
    return false;
  }
};
export const setJourneySink = (
  fn,
  predicate = () => true,
  context = () => null
) => {
  enabled = typeof fn === 'function';
  sink = enabled ? fn : () => {};
  canRecord = typeof predicate === 'function' ? predicate : () => false;
  correlationContext = typeof context === 'function' ? context : () => null;
};
const emit = (name, context) => {
  try {
    sink(name, context);
  } catch {
    /* Analytics must not change app behavior. */
  }
};
export const safeJourneyContext = (name, value) => {
  if (!value || typeof value !== 'object') return null;
  if (name === 'jeddai_interaction') {
    if (!INTERACTIONS.has(value.interaction) || !CONTROLS.has(value.control))
      return null;
    const result = { interaction: value.interaction, control: value.control };
    if (TARGET_VALUES.has(value.target)) result.target = value.target;
    if (
      typeof value.element_path === 'string' &&
      /^(?:[a-z]+:[1-9][0-9]?)(?:\/(?:[a-z]+:[1-9][0-9]?)){0,3}$/.test(
        value.element_path
      ) &&
      value.element_path
        .split('/')
        .every((part) => PATH_TAGS.has(part.split(':')[0]))
    )
      result.element_path = value.element_path;
    return result;
  }
  if (name === 'jeddai_operation') {
    if (!OPERATIONS.has(value.operation) || !PHASES.has(value.phase))
      return null;
    const result = { operation: value.operation, phase: value.phase };
    if (
      typeof value.operation_id === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value.operation_id
      )
    )
      result.operation_id = value.operation_id;
    if (
      Number.isInteger(value.duration_ms) &&
      value.duration_ms >= 0 &&
      value.duration_ms <= 600000
    )
      result.duration_ms = value.duration_ms;
    if (
      Number.isInteger(value.http_status) &&
      value.http_status >= 100 &&
      value.http_status <= 599
    )
      result.http_status = value.http_status;
    return result;
  }
  return null;
};
const structuralPath = (element) => {
  const parts = [];
  for (
    let node = element;
    node && parts.length < 4;
    node = node.parentElement
  ) {
    const tag = node.tagName?.toLowerCase();
    if (!PATH_TAGS.has(tag)) break;
    let index = 1;
    let inspected = 0;
    for (
      let sibling = node.previousElementSibling;
      sibling;
      sibling = sibling.previousElementSibling
    ) {
      inspected += 1;
      if (sibling.tagName === node.tagName) index += 1;
      if (index > 99 || inspected >= 200) break;
    }
    if (index > 99 || inspected >= 200) break;
    parts.push(`${tag}:${index}`);
  }
  return parts.join('/');
};
export const describeJourneyInteraction = (interaction, target) => {
  if (!INTERACTIONS.has(interaction)) return null;
  let element = target?.nodeType === 1 ? target : target?.parentElement;
  if (!element?.closest) return null;
  const control =
    element.closest(
      'button,a,select,input,textarea,form,[role="button"],[role="tab"],[role="checkbox"],[role="switch"]'
    ) || element;
  const tag = control.tagName;
  const role = control.getAttribute('role');
  let kind = 'surface';
  if (interaction === 'submit') kind = 'form';
  else if (tag === 'INPUT') {
    const type = (control.getAttribute('type') || 'text').toLowerCase();
    if (['password', 'email', 'tel', 'file', 'hidden'].includes(type))
      return null;
    if (type === 'checkbox' || type === 'radio') kind = 'checkbox';
    else if (['button', 'submit', 'reset'].includes(type)) kind = 'button';
    else if (interaction === 'change') kind = 'text_input';
    else return null;
  } else if (tag === 'TEXTAREA') {
    if (interaction !== 'change') return null;
    kind = 'text_input';
  } else if (control.isContentEditable) return null;
  else if (tag === 'A') kind = 'link';
  else if (tag === 'SELECT') kind = 'select';
  else if (tag === 'FORM') kind = 'form';
  else if (role === 'tab') kind = 'tab';
  else if (role === 'checkbox' || role === 'switch') kind = 'checkbox';
  else if (tag === 'BUTTON' || role === 'button') kind = 'button';
  if (
    interaction === 'change' &&
    !['checkbox', 'select', 'text_input'].includes(kind)
  )
    return null;
  // Only compare short, explicit labels; never send the raw string.
  const hint = control.getAttribute('data-journey-action');
  const explicit = TARGET_VALUES.has(hint) ? hint : undefined;
  const raw = ['button', 'link', 'tab'].includes(kind)
    ? (control.textContent || '').trim().toLowerCase()
    : '';
  const mapped = raw.length <= 40 ? TARGETS[raw] : undefined;
  return safeJourneyContext('jeddai_interaction', {
    interaction,
    control: kind,
    target: explicit || mapped,
    element_path: structuralPath(control)
  });
};
export const installJourneyInteractions = () => {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  ['click', 'change', 'submit'].forEach((interaction) => {
    document.addEventListener(
      interaction,
      (event) => {
        try {
          if (!event.isTrusted || !recordingActive()) return;
          const context = describeJourneyInteraction(interaction, event.target);
          if (context && interactionAllowed())
            emit('jeddai_interaction', context);
        } catch {
          /* Ignore instrumentation errors. */
        }
      },
      true
    );
  });
};
const API_RULES = [
  [/^\/script-credits\/checkout\/?$/, 'POST', 'credits_checkout_request'],
  [/^\/script-credits\/invoice\/?$/, 'POST', 'credits_invoice_request'],
  [
    /^\/script-credits\/callback-request\/?$/,
    'POST',
    'credits_callback_request'
  ],
  [/^\/assignments\/[^/]+\/markReopened\/?$/, 'PATCH', 'task_reopen'],
  [/^\/assignments\/[^/]+\/delete\/?$/, 'PATCH', 'task_delete'],
  [/^\/assignments\/[^/]+\/submissions\/batch\/?$/, 'POST', 'submission_batch'],
  [
    /^\/assignments\/[^/]+\/submissions\/extractText\/?$/,
    'POST',
    'extraction_request'
  ],
  [
    /^\/submissions\/[^/]+\/jeddAIFeedback\/retryDelivery\/?$/,
    'POST',
    'feedback_delivery_retry'
  ],
  [
    /^\/submissions\/[^/]+\/(?:download|downloadAll)\/?$/,
    'GET',
    'submission_export'
  ],
  [/^\/exams\/[^/]+\/exports(?:\/[^/]+){1,3}\/?$/, 'GET', 'exam_export'],
  [
    /^\/exams\/[^/]+\/(?:feedback\/(?:generate|retry)|marking(?:\/[^/]+){0,2})\/?$/,
    'POST',
    'exam_marking_request'
  ],
  [/^\/assignments\/?$/, 'POST', 'task_create'],
  [
    /^\/assignments\/[^/]+\/(?:publish|schedulePublish)\/?$/,
    'PATCH',
    'task_publish'
  ],
  [/^\/assignments\/[^/]+\/markClosed\/?$/, 'PATCH', 'task_close'],
  [/^\/assignments\/[^/]+\/jeddAIFeedback\/?$/, 'POST', 'feedback_request'],
  [
    /^\/assignments\/[^/]+(?:\/(?:title|display-title|updateSubject))?\/?$/,
    '*',
    'task_update'
  ],
  [/^\/submissions\/?$/, 'POST', 'draft_or_submission_create'],
  [
    /^\/students\/portfolio\/documents\/?$/,
    'POST',
    'draft_or_submission_create'
  ],
  [/^\/submissions\/[^/]+\/jeddAIFeedback\/?$/, 'POST', 'feedback_request'],
  [/^\/submissions\/[^/]+\/submit\/?$/, 'PATCH', 'submission_submit'],
  [/^\/submissions\/[^/]+\/delete\/?$/, 'PATCH', 'submission_delete'],
  [/^\/submissions\/[^/]+\/feedbacks(?:\/[^/]+)?\/?$/, '*', 'feedback_comment'],
  [
    /^\/submissions\/[^/]+\/(?:answers|reviewed|beginRevision|requestResubmission|reassign|updateDocumentType|updateYear|subjectSettings)(?:\/[^/]+)?\/?$/,
    '*',
    'submission_update'
  ],
  [/^\/teachers\/markingCriteria\/?$/, 'POST', 'rubric_create'],
  [/^\/teachers\/markingCriteria\/[^/]+\/?$/, '*', 'rubric_update'],
  [/^\/commentbanks(?:\/[^/]+)?\/?$/, '*', 'comment_bank_change'],
  [
    /^\/teachers\/successCriteria(?:\/[^/]+)?\/?$/,
    '*',
    'success_criteria_change'
  ],
  [
    /^\/(?:teachers\/)?shortAnswers?(?:\/[^/]+)?\/?$/i,
    '*',
    'short_answer_change'
  ],
  [/^\/(?:classes|classsettings)(?:\/[^/]+){0,2}\/?$/, '*', 'class_change'],
  [
    /^\/(?:advanced-marking|preferences|settings)(?:\/[^/]+){0,4}\/?$/,
    '*',
    'settings_change'
  ],
  [/^\/exams\/?$/, 'POST', 'exam_create'],
  [/^\/exams\/[^/]+\/done\/?$/, '*', 'exam_complete'],
  [/^\/exams\/[^/]+(?:\/[^/]+){0,3}\/?$/, '*', 'exam_update'],
  [/^\/ingestion\/[^/]+(?:\/[^/]+){0,3}\/?$/, '*', 'exam_ingestion_request']
];
export const classifyJourneyRequest = (url, method = 'GET') => {
  try {
    const verb = String(method).toUpperCase();
    if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(verb)) return null;
    const pathname = new URL(url, 'https://api.invalid').pathname;
    if (verb === 'GET') {
      return (
        API_RULES.find(
          ([pattern, match]) => match === 'GET' && pattern.test(pathname)
        )?.[2] || null
      );
    }
    return (
      API_RULES.find(
        ([pattern, match]) =>
          (match === '*' || match === verb) && pattern.test(pathname)
      )?.[2] || null
    );
  } catch {
    return null;
  }
};
const observedCompletions = new Set();
const feedbackCorrelations = new Map();
const observedOrders = new Map();
let journeyEpoch = 0;
export const getJourneyEpoch = () => journeyEpoch;
export const resetJourneyObservationCache = () => {
  journeyEpoch += 1;
  observedCompletions.clear();
  feedbackCorrelations.clear();
  observedOrders.clear();
};
export const trackObservedJourneyOutcome = (
  operation,
  phase,
  identity,
  correlationKey
) => {
  if (!recordingActive() || !identity || observedCompletions.has(identity))
    return;
  observedCompletions.add(identity);
  if (observedCompletions.size > 500)
    observedCompletions.delete(observedCompletions.values().next().value);
  const operation_id = correlationKey
    ? feedbackCorrelations.get(correlationKey)
    : undefined;
  if (correlationKey) feedbackCorrelations.delete(correlationKey);
  trackJourneyOutcome(operation, phase, operation_id);
};
export const trackJourneyOutcome = (operation, phase, operation_id) => {
  if (OPERATIONS.has(operation) && PHASES.has(phase))
    emit(
      'jeddai_operation',
      safeJourneyContext('jeddai_operation', {
        operation,
        phase,
        operation_id
      })
    );
};
// Only a PENDING → granted/failed transition in this page is counted; mounting
// on an already settled order is a baseline, not a fresh purchase.
export const observeJourneyOrder = (path, order) => {
  try {
    if (!recordingActive() || !order || typeof order !== 'object') return;
    const match = new URL(path, 'https://api.invalid').pathname.match(
      /^\/script-credits\/orders\/([^/]+)\/?$/
    );
    if (!match) return;
    const state =
      order.granted === true
        ? 'granted'
        : ['FAILED', 'EXPIRED', 'VOID'].includes(order.status)
        ? 'failed'
        : 'pending';
    const previous = observedOrders.get(match[1]);
    observedOrders.set(match[1], state);
    if (observedOrders.size > 200)
      observedOrders.delete(observedOrders.keys().next().value);
    if (previous === 'pending' && state !== 'pending')
      trackObservedJourneyOutcome(
        'credits_granted',
        state === 'granted' ? 'completed' : 'failure',
        `order:${match[1]}:${state}`
      );
  } catch {
    /* Never affect the order poll. */
  }
};
export const startJourneyOperation = (operation, url = '', requestedId) => {
  if (!recordingActive() || !OPERATIONS.has(operation)) return () => {};
  const started = Date.now();
  const startedEpoch = journeyEpoch;
  let operation_id;
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function')
      operation_id = UUID.test(requestedId || '')
        ? requestedId
        : globalThis.crypto.randomUUID();
    if (!UUID.test(operation_id || '')) operation_id = undefined;
  } catch {
    /* optional correlation */
  }
  let correlationKey;
  if (operation === 'feedback_request' && operation_id) {
    try {
      const path = new URL(url, 'https://api.invalid').pathname;
      const match = path.match(/^\/submissions\/([^/]+)\/jeddAIFeedback\/?$/);
      if (match) correlationKey = `submission:${decodeURIComponent(match[1])}`;
    } catch {
      /* Correlation is optional. */
    }
    if (correlationKey) {
      feedbackCorrelations.set(correlationKey, operation_id);
      if (feedbackCorrelations.size > 200)
        feedbackCorrelations.delete(feedbackCorrelations.keys().next().value);
    }
  }
  emit(
    'jeddai_operation',
    safeJourneyContext('jeddai_operation', {
      operation,
      phase: 'attempt',
      operation_id
    })
  );
  let ended = false;
  const finish = (phase, status) => {
    if (
      ended ||
      startedEpoch !== journeyEpoch ||
      !recordingActive() ||
      !PHASES.has(phase) ||
      phase === 'attempt'
    )
      return;
    ended = true;
    if (
      phase === 'failure' &&
      correlationKey &&
      feedbackCorrelations.get(correlationKey) === operation_id
    )
      feedbackCorrelations.delete(correlationKey);
    const duration_ms = Math.min(600000, Math.max(0, Date.now() - started));
    emit(
      'jeddai_operation',
      safeJourneyContext('jeddai_operation', {
        operation,
        phase,
        duration_ms,
        http_status: status,
        operation_id
      })
    );
  };
  finish.operationId = operation_id;
  return finish;
};
export const startJourneyRequest = (url, method, requestedId) => {
  const operation = classifyJourneyRequest(url, method);
  return operation
    ? startJourneyOperation(
        operation,
        url,
        operation === 'feedback_request' ? requestedId : undefined
      )
    : () => {};
};
export const instrumentJourneyMutation = async (
  url,
  method,
  run,
  options = {}
) => {
  if (!recordingActive()) return run();
  const finish = startJourneyRequest(url, method, options.feedbackOperationId);
  try {
    const result = await run(
      journeyCorrelationHeaders(
        url,
        method,
        finish.operationId,
        options.feedbackOperationId
      )
    );
    const status = result?.status;
    const body = result?.data;
    const failed =
      result?.errorMessage ||
      (typeof status === 'number' && status >= 400) ||
      body?.errorMessage ||
      body?.ok === false ||
      body?.success === false;
    finish(
      failed
        ? 'failure'
        : body == null
        ? 'unconfirmed'
        : status === 202 ||
          classifyJourneyRequest(url, method) === 'feedback_request'
        ? 'accepted'
        : 'success',
      status
    );
    return result;
  } catch (error) {
    finish('failure', error?.status);
    throw error;
  }
};

// Keep this list in step with server-side durable outcome capture.
const CORRELATION_ROUTES = [
  ['POST', /^\/assignments$/],
  [
    'PUT',
    /^\/assignments\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
  ],
  ['POST', /^\/students\/portfolio\/documents$/],
  ['PATCH', /^\/assignments\/[^/]+\/publish$/],
  ['POST', /^\/submissions\/[^/]+\/jeddAIFeedback$/]
];
export const journeyCorrelationHeaders = (
  url,
  method,
  operationId,
  feedbackOperationId
) => {
  try {
    if (!recordingActive() || !UUID.test(operationId || '')) return undefined;
    const context = correlationContext();
    if (!UUID.test(context?.journeyId || '')) return undefined;
    const base = new URL(process.env.REACT_APP_API_BASE_URL);
    const target = new URL(url, window.location.href);
    if (
      base.protocol !== 'https:' ||
      base.username ||
      base.password ||
      target.origin !== base.origin ||
      target.username ||
      target.password ||
      (/^\/submissions\/[^/]+\/jeddAIFeedback$/.test(target.pathname) &&
        feedbackOperationId !== operationId) ||
      !CORRELATION_ROUTES.some(
        ([verb, path]) =>
          verb === String(method).toUpperCase() && path.test(target.pathname)
      )
    )
      return undefined;
    return {
      'X-Jeddle-Journey-Id': context.journeyId,
      'X-Jeddle-Operation-Id': operationId
    };
  } catch {
    return undefined;
  }
};
